<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-cash-multiple me-2"></i>{{ t('billing.title','Billing') }} &amp; Payments</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab === 'invoices'" class="hms-btn hms-btn-primary" @click="openNewInvoice">
            <i class="mdi mdi-file-plus me-1"></i>New Invoice
          </button>
          <button v-if="tab === 'services'" class="hms-btn hms-btn-primary" @click="openAddService">
            <i class="mdi mdi-plus me-1"></i>Add Service
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setStatusFilter('')" :class="{ selected: tab==='invoices' && statusFilter==='' }">
          <div class="hms-stat-num">{{ invoices.length }}</div>
          <div class="hms-stat-lbl">Total Invoices</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('paid')" :class="{ selected: tab==='invoices' && statusFilter==='paid' }">
          <div class="hms-stat-num hms-stat-green">{{ countByStatus('paid') }}</div>
          <div class="hms-stat-lbl">Paid</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('partial')" :class="{ selected: tab==='invoices' && statusFilter==='partial' }">
          <div class="hms-stat-num hms-stat-info">{{ countByStatus('partial') }}</div>
          <div class="hms-stat-lbl">Partial</div>
        </div>
        <div class="hms-stat" @click="setStatusFilter('unpaid')" :class="{ selected: tab==='invoices' && statusFilter==='unpaid' }">
          <div class="hms-stat-num hms-stat-amber">{{ countByStatus('unpaid') }}</div>
          <div class="hms-stat-lbl">Unpaid</div>
        </div>
        <div class="hms-stat" v-if="countByStatus('insurance_pending') > 0" @click="setStatusFilter('insurance_pending')" :class="{ selected: tab==='invoices' && statusFilter==='insurance_pending' }">
          <div class="hms-stat-num hms-stat-info">{{ countByStatus('insurance_pending') }}</div>
          <div class="hms-stat-lbl">Insurance Pending</div>
        </div>
        <div class="hms-stat" v-if="countByStatus('waived') > 0" @click="setStatusFilter('waived')" :class="{ selected: tab==='invoices' && statusFilter==='waived' }">
          <div class="hms-stat-num">{{ countByStatus('waived') }}</div>
          <div class="hms-stat-lbl">Waived</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-purple">KES {{ formatMoney(totalCollected) }}</div>
          <div class="hms-stat-lbl">Collected</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-red">KES {{ formatMoney(totalOutstanding) }}</div>
          <div class="hms-stat-lbl">Outstanding</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'invoices' }" @click="tab = 'invoices'">
          <i class="mdi mdi-receipt-text me-1"></i>Invoices
        </button>
        <button class="hms-tab" :class="{ active: tab === 'payments' }" @click="tab = 'payments'">
          <i class="mdi mdi-cash-check me-1"></i>Payments
        </button>
        <button class="hms-tab" :class="{ active: tab === 'services' }" @click="tab = 'services'">
          <i class="mdi mdi-format-list-bulleted-type me-1"></i>Service Catalog
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ INVOICES TAB ============ -->
      <div v-else-if="tab === 'invoices'">
        <!-- Patient focus chip (shown when filtering by a specific patient) -->
        <div v-if="focusedPatient" class="hms-card hms-focus-card mb-3">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <i class="mdi mdi-account-circle hms-focus-icon"></i>
            <div class="flex-grow-1">
              <div class="hms-text fw-semibold">Focused on {{ focusedPatient.first_name }} {{ focusedPatient.last_name }}</div>
              <div class="hms-muted-sm">
                <span class="hms-mono">{{ focusedPatient.patient_no }}</span>
                <span v-if="focusedPatient.phone"> · {{ focusedPatient.phone }}</span>
                · {{ focusedPatientStats.invoice_count }} invoices
                · KES {{ formatMoney(focusedPatientStats.total_billed) }} billed
                · KES {{ formatMoney(focusedPatientStats.total_paid) }} paid
                · <strong :class="parseFloat(focusedPatientStats.total_outstanding) > 0 ? 'hms-text-danger' : 'hms-text-success'">KES {{ formatMoney(focusedPatientStats.total_outstanding) }} outstanding</strong>
              </div>
            </div>
            <button class="hms-btn hms-btn-primary hms-btn-sm" @click="openNewInvoiceForFocused">
              <i class="mdi mdi-file-plus me-1"></i>New invoice for {{ focusedPatient.first_name }}
            </button>
            <button class="hms-btn hms-btn-blue hms-btn-sm" @click="$router.push('/patients/' + focusedPatient.patient_id + '/chart')">
              <i class="mdi mdi-folder-account-outline me-1"></i>Open chart
            </button>
            <button class="hms-btn hms-btn-red hms-btn-sm" @click="clearFocusedPatient" title="Clear focus">
              <i class="mdi mdi-close"></i>
            </button>
          </div>
        </div>

        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <!-- Focus-on-patient typeahead (only shown when not already focused) -->
            <div v-if="!focusedPatient" class="hms-search-wrap" style="position:relative;">
              <i class="mdi mdi-account-search"></i>
              <input class="hms-search" v-model="focusSearch" @input="onFocusSearch" placeholder="Focus on patient — name, PAT no, phone…" />
              <div v-if="focusResults.length" class="hms-suggest" style="position:absolute; top:100%; left:0; right:0; z-index:5;">
                <div v-for="pp in focusResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickFocusedPatient(pp)">
                  <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                  <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }}</div>
                </div>
              </div>
            </div>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="invSearch" placeholder="Search invoice number…" />
            </div>
            <select class="hms-input hms-filter" v-model="statusFilter">
              <option value="">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="waived">Waived</option>
              <option value="insurance_pending">Insurance Pending</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredInvoices.length }} invoices</span>
          </div>

          <div v-if="filteredInvoices.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Invoice</th>
                <th>Patient</th>
                <th>Date</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="inv in pagedInvoices" :key="inv.invoice_id">
                  <td><span class="hms-mono">{{ inv.invoice_number }}</span></td>
                  <td>
                    <div class="hms-text fw-semibold">{{ inv.patient_name }}</div>
                    <div class="hms-muted-sm">{{ inv.patient_no }}</div>
                  </td>
                  <td>{{ formatDate(inv.invoice_date) }}</td>
                  <td>KES {{ formatMoney(inv.total_amount) }}</td>
                  <td class="hms-text-success">KES {{ formatMoney(inv.paid_amount) }}</td>
                  <td :class="parseFloat(inv.balance) > 0 ? 'hms-text-danger fw-bold' : 'hms-muted-sm'">
                    KES {{ formatMoney(inv.balance) }}
                  </td>
                  <td><span class="hms-badge" :class="payStatusBadge(inv.payment_status)">{{ payStatusLabel(inv.payment_status) }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="openInvoice(inv)" title="View">
                      <i class="mdi mdi-eye"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="printInvoice(inv.invoice_id)" title="Print invoice">
                      <i class="mdi mdi-printer"></i>
                    </button>
                    <button v-if="parseFloat(inv.balance) > 0" class="hms-btn hms-btn-sm hms-btn-primary" @click="openPayment(inv)" title="Record payment">
                      <i class="mdi mdi-cash-plus me-1"></i>Pay
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="invPageCount > 1" class="hms-pager">
              <button class="hms-btn hms-btn-sm hms-btn-blue" :disabled="invPage <= 1" @click="invPage--"><i class="mdi mdi-chevron-left"></i> Prev</button>
              <span class="hms-muted">Page {{ Math.min(invPage, invPageCount) }} of {{ invPageCount }}</span>
              <button class="hms-btn hms-btn-sm hms-btn-blue" :disabled="invPage >= invPageCount" @click="invPage++">Next <i class="mdi mdi-chevron-right"></i></button>
            </div>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-receipt-text-outline"></i>No invoices match.</div>
        </div>
      </div>

      <!-- ============ PAYMENTS TAB ============ -->
      <div v-else-if="tab === 'payments'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="paySearch" placeholder="Search patient, payment number, M-Pesa ref..." />
            </div>
            <select class="hms-input hms-filter" v-model="payMethodFilter">
              <option value="">All methods</option>
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="insurance">Insurance</option>
              <option value="corporate">Corporate</option>
              <option value="waiver">Waiver</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredPayments.length }} payments · KES {{ formatMoney(filteredPaymentsTotal) }}</span>
          </div>

          <div v-if="filteredPayments.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Receipt</th>
                <th>Patient</th>
                <th>Date</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                <tr v-for="p in pagedPayments" :key="p.payment_id">
                  <td><span class="hms-mono">{{ p.payment_number }}</span></td>
                  <td>
                    <div class="hms-text fw-semibold">{{ p.patient_name }}</div>
                    <div class="hms-muted-sm">{{ p.patient_no }}</div>
                  </td>
                  <td>{{ formatDateTime(p.payment_date) }}</td>
                  <td><span class="hms-badge" :class="methodBadge(p.payment_method)"><i :class="methodIcon(p.payment_method)" class="me-1"></i>{{ formatMethod(p.payment_method) }}</span></td>
                  <td><span class="hms-mono">{{ p.mpesa_reference || p.card_reference || p.bank_reference || '—' }}</span></td>
                  <td class="hms-text-success fw-semibold">KES {{ formatMoney(p.amount) }}</td>
                  <td><span class="hms-badge" :class="payTxnStatusBadge(p.status)">{{ p.status }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="printReceipt(p.payment_id)" title="Print receipt">
                      <i class="mdi mdi-printer"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="payPageCount > 1" class="hms-pager">
              <button class="hms-btn hms-btn-sm hms-btn-blue" :disabled="payPage <= 1" @click="payPage--"><i class="mdi mdi-chevron-left"></i> Prev</button>
              <span class="hms-muted">Page {{ Math.min(payPage, payPageCount) }} of {{ payPageCount }}</span>
              <button class="hms-btn hms-btn-sm hms-btn-blue" :disabled="payPage >= payPageCount" @click="payPage++">Next <i class="mdi mdi-chevron-right"></i></button>
            </div>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-cash-remove"></i>No payments recorded.</div>
        </div>
      </div>

      <!-- ============ SERVICES TAB ============ -->
      <div v-else-if="tab === 'services'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="svcSearch" placeholder="Search service name, code..." />
            </div>
            <select class="hms-input hms-filter" v-model="svcCategoryFilter">
              <option value="">All categories</option>
              <option value="consultation">Consultation</option>
              <option value="procedure">Procedure</option>
              <option value="lab">Lab</option>
              <option value="radiology">Radiology</option>
              <option value="pharmacy">Pharmacy</option>
              <option value="ward">Ward</option>
              <option value="theatre">Theatre</option>
              <option value="other">Other</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredServices.length }} services</span>
          </div>

          <div v-if="filteredServices.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Service</th>
                <th>Code</th>
                <th>Category</th>
                <th>Cash Price</th>
                <th>Insurance Price</th>
                <th>Tax</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                <tr v-for="s in filteredServices" :key="s.service_id">
                  <td><span class="hms-text fw-semibold">{{ s.service_name }}</span></td>
                  <td><span class="hms-mono">{{ s.service_code || '—' }}</span></td>
                  <td><span class="hms-badge hms-badge-info">{{ s.category }}</span></td>
                  <td>KES {{ formatMoney(s.price) }}</td>
                  <td>{{ s.insurance_price ? 'KES ' + formatMoney(s.insurance_price) : '—' }}</td>
                  <td>{{ s.is_taxable == 1 ? (s.tax_rate + '%') : '—' }}</td>
                  <td><span class="hms-badge" :class="s.status === 'active' ? 'hms-badge-success' : 'hms-badge-neutral'">{{ s.status }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty"><i class="mdi mdi-format-list-bulleted-type"></i>No services in catalog.</div>
        </div>
      </div>

      <!-- ============ INVOICE DETAIL MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showInvoice" @click.self="closeInvoice">
        <div class="hms-modal" v-if="invoiceDetail || invoiceLoading">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-receipt-text me-2"></i>{{ invoiceDetail ? invoiceDetail.invoice_number : 'Invoice' }}</span>
            <button class="hms-modal-close" @click="closeInvoice">&times;</button>
          </div>

          <div v-if="invoiceLoading" class="hms-spinner py-3"><div class="spinner-border"></div></div>

          <div v-else-if="invoiceDetail">
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Patient</span><span>{{ invoiceDetail.patient_name }} ({{ invoiceDetail.patient_no }})</span></div>
              <div><span class="hms-kv-key">Phone</span><span>{{ invoiceDetail.phone || '—' }}</span></div>
              <div><span class="hms-kv-key">Date</span><span>{{ formatDateTime(invoiceDetail.invoice_date) }}</span></div>
              <div v-if="invoiceDetail.due_date"><span class="hms-kv-key">Due</span><span>{{ formatDate(invoiceDetail.due_date) }}</span></div>
              <div v-if="invoiceDetail.insurance_provider"><span class="hms-kv-key">Insurance</span><span>{{ invoiceDetail.insurance_provider }}</span></div>
              <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="payStatusBadge(invoiceDetail.payment_status)">{{ payStatusLabel(invoiceDetail.payment_status) }}</span></span></div>
            </div>

            <div class="hms-section-label">LINE ITEMS</div>
            <table class="hms-table">
              <thead><tr><th>Description</th><th>Category</th><th>Qty</th><th>Unit</th><th>Disc</th><th>Total</th></tr></thead>
              <tbody>
                <template v-for="line in invoiceItems" :key="line.item_id">
                  <tr>
                    <td>
                      {{ line.item_description }}
                      <span v-if="line.rate_card_id" class="hms-rate-pill ms-1" :title="line.rate_card_name + ' — ' + line.rate_rule">
                        <i class="mdi mdi-handshake-outline me-1"></i>{{ line.rate_card_provider }}
                      </span>
                    </td>
                    <td><span class="hms-badge hms-badge-neutral">{{ line.item_category }}</span></td>
                    <td>{{ line.quantity }}</td>
                    <td>
                      <div v-if="line.catalogue_price !== null && line.catalogue_price !== undefined && Number(line.catalogue_price) !== Number(line.unit_price)">
                        <span class="hms-strike hms-muted-sm">KES {{ formatMoney(line.catalogue_price) }}</span>
                        <div class="fw-semibold">KES {{ formatMoney(line.unit_price) }}</div>
                      </div>
                      <span v-else>KES {{ formatMoney(line.unit_price) }}</span>
                    </td>
                    <td>{{ line.discount > 0 ? 'KES ' + formatMoney(line.discount) : '—' }}</td>
                    <td class="hms-text fw-semibold">KES {{ formatMoney(line.total_price) }}</td>
                  </tr>
                  <tr v-if="line.rate_card_id && line.catalogue_price !== null && Number(line.catalogue_price) > Number(line.unit_price)" class="hms-rate-row">
                    <td colspan="6">
                      <span class="hms-rate-savings">
                        <i class="mdi mdi-shield-check-outline me-1"></i>
                        {{ line.rate_card_provider }} rate applied
                        <span v-if="line.rate_card_name" class="hms-muted-sm">({{ line.rate_card_name }})</span>:
                        catalogue <strong>KES {{ formatMoney(line.catalogue_price) }}</strong> →
                        <strong>KES {{ formatMoney(line.unit_price) }}</strong>
                        · save <strong>KES {{ formatMoney(Number(line.catalogue_price) - Number(line.unit_price)) }}</strong>
                        per unit
                        <span class="hms-muted-sm">· rule: {{ line.rate_rule }}</span>
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>

            <div class="hms-totals">
              <div><span>Subtotal</span><span>KES {{ formatMoney(invoiceDetail.subtotal) }}</span></div>
              <div v-if="parseFloat(invoiceDetail.tax_amount) > 0"><span>Tax</span><span>KES {{ formatMoney(invoiceDetail.tax_amount) }}</span></div>
              <div v-if="parseFloat(invoiceDetail.discount_amount) > 0"><span>Discount</span><span class="hms-text-success">- KES {{ formatMoney(invoiceDetail.discount_amount) }}</span></div>
              <div v-if="insuranceSavings && insuranceSavings.total_savings > 0" class="hms-insurance-summary">
                <span><i class="mdi mdi-shield-check-outline me-1"></i>Insurance savings (this invoice)</span>
                <span class="hms-text-success">
                  KES {{ formatMoney(insuranceSavings.total_savings) }}
                  <span class="hms-muted-sm">({{ insuranceSavings.lines_with_rate }} line<span v-if="insuranceSavings.lines_with_rate !== 1">s</span>)</span>
                </span>
              </div>
              <div class="hms-totals-total"><span>Total</span><span>KES {{ formatMoney(invoiceDetail.total_amount) }}</span></div>
              <div><span>Paid</span><span class="hms-text-success">KES {{ formatMoney(invoiceDetail.paid_amount) }}</span></div>
              <div class="hms-totals-balance"><span>Balance</span><span :class="parseFloat(invoiceDetail.balance) > 0 ? 'hms-text-danger' : 'hms-text-success'">KES {{ formatMoney(invoiceDetail.balance) }}</span></div>
            </div>

            <div v-if="invoicePayments.length">
              <div class="hms-section-label">PAYMENT HISTORY</div>
              <table class="hms-table">
                <thead><tr><th>Receipt</th><th>Date</th><th>Method</th><th>Reference</th><th>Amount</th></tr></thead>
                <tbody>
                  <tr v-for="p in invoicePayments" :key="p.payment_id">
                    <td><span class="hms-mono">{{ p.payment_number }}</span></td>
                    <td>{{ formatDateTime(p.payment_date) }}</td>
                    <td><span class="hms-badge" :class="methodBadge(p.payment_method)">{{ formatMethod(p.payment_method) }}</span></td>
                    <td><span class="hms-mono">{{ p.mpesa_reference || p.card_reference || p.bank_reference || '—' }}</span></td>
                    <td class="hms-text-success fw-semibold">KES {{ formatMoney(p.amount) }}</td>
                    <td>
                      <button class="hms-btn hms-btn-sm hms-btn-blue" @click="printReceipt(p.payment_id)" title="Print receipt">
                        <i class="mdi mdi-printer"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button v-if="invoiceDetail.payment_status !== 'paid'" class="hms-btn hms-btn-blue" @click="openAddItems">
                <i class="mdi mdi-playlist-plus me-1"></i>Add Items
              </button>
              <button v-if="parseFloat(invoiceDetail.balance) > 0" class="hms-btn hms-btn-primary" @click="openPayment(invoiceDetail)">
                <i class="mdi mdi-cash-plus me-1"></i>{{ t('billing.record_payment','Record Payment') }}
              </button>
              <button v-if="parseFloat(invoiceDetail.balance) > 0" class="hms-btn hms-btn-green" @click="openStk(invoiceDetail)">
                <i class="mdi mdi-cellphone-wireless me-1"></i>Pay via M-Pesa (STK)
              </button>
              <button v-if="parseFloat(invoiceDetail.balance) > 0" class="hms-btn hms-btn-blue" :disabled="sendingPayLink" @click="sendPayLink(invoiceDetail)">
                <i class="mdi" :class="sendingPayLink ? 'mdi-loading mdi-spin' : 'mdi-share-variant'"></i>
                {{ sendingPayLink ? ' Sending…' : ' Send pay link (SMS+email)' }}
              </button>
              <button class="hms-btn hms-btn-amber" @click="openDiscount">
                <i class="mdi mdi-tag-outline me-1"></i>Apply Discount
              </button>
              <button class="hms-btn hms-btn-blue ms-auto" @click="printInvoice(invoiceDetail.invoice_id)">
                <i class="mdi mdi-printer me-1"></i>Print Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ RECORD PAYMENT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showPayment" @click.self="showPayment=false">
        <div class="hms-modal" style="max-width:540px;" v-if="paymentForm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-cash-plus me-2"></i>{{ t('billing.record_payment','Record Payment') }}</span>
            <button class="hms-modal-close" @click="showPayment=false">&times;</button>
          </div>
          <div v-if="paymentError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ paymentError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Invoice</span><span class="hms-mono">{{ paymentForm.invoice_number }}</span></div>
            <div><span class="hms-kv-key">Patient</span><span>{{ paymentForm.patient_name }}</span></div>
            <div><span class="hms-kv-key">Total</span><span>KES {{ formatMoney(paymentForm.total_amount) }}</span></div>
            <div><span class="hms-kv-key">Already Paid</span><span class="hms-text-success">KES {{ formatMoney(paymentForm.paid_amount) }}</span></div>
            <div><span class="hms-kv-key">Balance</span><span class="hms-text-danger fw-bold">KES {{ formatMoney(paymentForm.balance) }}</span></div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Amount (KES) *</label>
              <input type="number" class="hms-input" v-model.number="paymentForm.amount" min="0" :max="paymentForm.balance" step="0.01" />
              <button class="hms-btn-link" @click="paymentForm.amount = parseFloat(paymentForm.balance)">Pay full balance</button>
            </div>
            <div class="col-md-6"><label class="hms-label">Method *</label>
              <select class="hms-input" v-model="paymentForm.payment_method">
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="insurance">Insurance</option>
                <option value="corporate">Corporate</option>
                <option value="waiver">Waiver</option>
              </select>
            </div>
            <div v-if="paymentForm.payment_method === 'mpesa'" class="col-12">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="hms-label m-0">M-Pesa Reference *</label>
                <button class="hms-btn hms-btn-green hms-btn-sm" type="button" @click="openStkFromPayment">
                  <i class="mdi mdi-cellphone-wireless me-1"></i>Send STK push instead
                </button>
              </div>
              <input class="hms-input" v-model="paymentForm.mpesa_reference" placeholder="Enter receipt code if customer already paid (e.g. TKL5XYZ123)" />
              <div class="hms-muted-sm mt-1">If the customer hasn't paid yet, click <strong>Send STK push</strong> to prompt their phone — the receipt will be recorded automatically.</div>
            </div>
            <div v-if="paymentForm.payment_method === 'card'" class="col-12"><label class="hms-label">Card Reference *</label>
              <input class="hms-input" v-model="paymentForm.card_reference" placeholder="Last 4 digits / auth code" />
            </div>
            <div v-if="paymentForm.payment_method === 'bank_transfer'" class="col-12"><label class="hms-label">Bank Reference *</label>
              <input class="hms-input" v-model="paymentForm.bank_reference" placeholder="Bank slip / transfer ref" />
            </div>
            <template v-if="paymentForm.payment_method === 'waiver'">
              <div class="col-md-6"><label class="hms-label">Waiver reason *</label>
                <select class="hms-input" v-model="paymentForm.waiver_reason">
                  <option value="">— Select —</option>
                  <option value="indigent">Indigent / inability to pay</option>
                  <option value="fistula">Fistula programme</option>
                  <option value="mortality">Deceased — waiver of balance</option>
                  <option value="programme_funded">Donor / programme funded</option>
                  <option value="staff">Staff or staff dependant</option>
                  <option value="disaster">Disaster / emergency response</option>
                  <option value="medico_legal">Medico-legal (police case)</option>
                  <option value="other">Other (specify)</option>
                </select>
              </div>
              <div class="col-md-6"><label class="hms-label">Supporting document (URL or path)</label>
                <input class="hms-input" v-model="paymentForm.waiver_doc" placeholder="e.g. /uploads/chief-letter-1234.pdf" />
              </div>
              <div class="col-12"><label class="hms-label">Narrative *</label>
                <textarea class="hms-input" rows="2" v-model="paymentForm.waiver_narrative" placeholder="Why is this being waived? Who authorised it?"></textarea>
              </div>
              <div class="col-12">
                <div class="hms-info-banner">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Recording a waiver opens a 3-step approval chain: <strong>Charge Nurse → Finance Officer → Medical Superintendent</strong>. Each step is audit-logged.
                </div>
              </div>
            </template>
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="paymentForm.notes" placeholder="Optional"></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-2">
            <SaveAndQueue ref="sqPayment" from-point="billing" :saving="processing" :disabled="!paymentForm.amount"
                          save-label="Record Payment" saving-label="Recording..."
                          btn-class="hms-btn hms-btn-primary" icon="mdi-cash-plus" @save="doRecordPayment" />
          </div>
        </div>
      </div>

      <!-- ============ DISCOUNT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDiscount" @click.self="showDiscount=false">
        <div class="hms-modal" style="max-width:420px;" v-if="invoiceDetail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-tag-outline me-2"></i>Apply Discount</span>
            <button class="hms-modal-close" @click="showDiscount=false">&times;</button>
          </div>
          <div v-if="discountError" class="hms-alert hms-alert-danger">{{ discountError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Invoice</span><span class="hms-mono">{{ invoiceDetail.invoice_number }}</span></div>
            <div><span class="hms-kv-key">Subtotal</span><span>KES {{ formatMoney(invoiceDetail.subtotal) }}</span></div>
            <div><span class="hms-kv-key">Current Discount</span><span>KES {{ formatMoney(invoiceDetail.discount_amount) }}</span></div>
          </div>

          <div class="mb-3"><label class="hms-label">New Discount Amount (KES)</label>
            <input type="number" class="hms-input" v-model.number="discountAmount" min="0" :max="invoiceDetail.subtotal" step="0.01" />
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doApplyDiscount" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Apply Discount
          </button>
        </div>
      </div>

      <!-- ============ ADD ITEMS TO EXISTING INVOICE ============ -->
      <div class="hms-modal-overlay" v-if="showAddItems" @click.self="showAddItems=false">
        <div class="hms-modal" v-if="addItems">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-playlist-plus me-2"></i>Add items to invoice</span>
            <button class="hms-modal-close" @click="showAddItems=false">&times;</button>
          </div>
          <div v-if="addItemsError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ addItemsError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Invoice</span><span class="hms-mono">{{ invoiceDetail && invoiceDetail.invoice_number }}</span></div>
            <div><span class="hms-kv-key">Patient</span><span>{{ invoiceDetail && invoiceDetail.patient_name }}</span></div>
            <div><span class="hms-kv-key">Current total</span><span>KES {{ formatMoney(invoiceDetail && invoiceDetail.total_amount) }}</span></div>
            <div><span class="hms-kv-key">Current balance</span><span class="hms-text-danger fw-bold">KES {{ formatMoney(invoiceDetail && invoiceDetail.balance) }}</span></div>
          </div>

          <div class="hms-section-label">NEW LINES</div>
          <div v-for="(line, li) in addItems.lines" :key="li" class="hms-inv-line">
            <div class="row g-2 align-items-end">
              <div class="col-md-5">
                <label class="hms-label" v-if="li === 0">Service / Description</label>
                <input class="hms-input" list="hms-services-list"
                       v-model="line._search"
                       @change="onServicePicked(line)"
                       placeholder="Type to search — e.g. lab, x-ray, pharmacy…" />
                <input v-if="!line.service_id" class="hms-input mt-1" v-model="line.item_description" placeholder="Custom description (free text)" />
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Qty</label>
                <input type="number" class="hms-input" v-model.number="line.quantity" min="1" />
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Unit Price</label>
                <input type="number" class="hms-input" v-model.number="line.unit_price" min="0" step="0.01" />
                <div v-if="line._rate_card" class="hms-rate-pill" :title="line._rate_card.card_name + ' — ' + line._rate_card.rule">
                  <i class="mdi mdi-handshake-outline me-1"></i>
                  {{ line._rate_card.provider_name }}: KES {{ formatMoney(line._rate_card.catalogue_price) }} → KES {{ formatMoney(line._rate_card.negotiated_price) }} <strong>(save {{ formatMoney(line._rate_card.savings) }})</strong>
                </div>
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Total</label>
                <div class="hms-input-readonly">KES {{ formatMoney(line.quantity * line.unit_price) }}</div>
              </div>
              <div class="col-md-1">
                <button class="hms-btn hms-btn-sm hms-btn-red" @click="removeAddLine(li)" :disabled="addItems.lines.length === 1">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>
          <button class="hms-btn hms-btn-blue hms-btn-sm mt-2" @click="addAddLine">
            <i class="mdi mdi-plus me-1"></i>Add another line
          </button>

          <div class="hms-totals mt-3">
            <div><span>Lines added</span><span>KES {{ formatMoney(addItemsSubtotal) }}</span></div>
            <div class="hms-totals-total"><span>New balance after</span><span class="hms-text-danger">KES {{ formatMoney(addItemsNewBalance) }}</span></div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-secondary flex-fill" @click="showAddItems=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="submitAddItems" :disabled="processing || addItemsSubtotal <= 0">
              <i class="mdi" :class="processing ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
              {{ processing ? ' Adding…' : ' Add to invoice' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ============ M-PESA STK PUSH MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showStk" @click.self="closeStk">
        <div class="hms-modal" style="max-width:520px;" v-if="stkForm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-cellphone-wireless me-2"></i>Pay via M-Pesa (STK)</span>
            <button class="hms-modal-close" @click="closeStk" :disabled="stkSending">&times;</button>
          </div>

          <!-- Step 1: form -->
          <div v-if="!stkStatus">
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Invoice</span><span class="hms-mono">{{ stkForm.invoice_number }}</span></div>
              <div><span class="hms-kv-key">Patient</span><span>{{ stkForm.patient_name }}</span></div>
              <div><span class="hms-kv-key">Balance</span><span class="hms-text-danger fw-bold">KES {{ formatMoney(stkForm.balance) }}</span></div>
            </div>

            <div v-if="stkError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ stkError }}</div>

            <div class="row g-3 mb-3">
              <div class="col-md-7">
                <label class="hms-label">Customer phone *</label>
                <input class="hms-input" v-model="stkForm.phone" placeholder="07XX XXX XXX" autofocus />
                <div class="hms-muted-sm mt-1">Accepts 07XX, 01XX, or 2547XX</div>
              </div>
              <div class="col-md-5">
                <label class="hms-label">Amount (KES) *</label>
                <input type="number" class="hms-input" v-model.number="stkForm.amount" min="1" :max="stkForm.balance" />
                <button class="hms-btn-link" @click="stkForm.amount = Math.floor(parseFloat(stkForm.balance))">Full balance</button>
              </div>
            </div>

            <div class="hms-alert hms-alert-info">
              <i class="mdi mdi-information-outline me-1"></i>
              The customer will get an STK prompt on their phone. They have ~90s to enter their M-Pesa PIN. The invoice updates automatically once payment is confirmed.
            </div>

            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-secondary" @click="closeStk" :disabled="stkSending">Cancel</button>
              <button class="hms-btn hms-btn-primary ms-auto" :disabled="!canSendInvoiceStk || stkSending" @click="sendInvoiceStk">
                <i class="mdi" :class="stkSending ? 'mdi-loading mdi-spin' : 'mdi-send'"></i>
                {{ stkSending ? 'Sending…' : 'Send STK push' }}
              </button>
            </div>
          </div>

          <!-- Step 2: status -->
          <div v-else>
            <div class="hms-stk-status" :class="stkOk === true ? 'hms-stk-ok' : (stkOk === false ? 'hms-stk-bad' : 'hms-stk-pending')">
              <div class="hms-stk-icon">
                <i v-if="stkOk === true" class="mdi mdi-check-circle"></i>
                <i v-else-if="stkOk === false" class="mdi mdi-close-circle"></i>
                <i v-else class="mdi mdi-loading mdi-spin"></i>
              </div>
              <div class="hms-stk-msg">{{ stkStatus }}</div>
            </div>
            <div v-if="stkCheckoutId" class="hms-muted-sm mt-2">CheckoutRequestID: <span class="hms-mono">{{ stkCheckoutId }}</span></div>

            <div class="d-flex gap-2 mt-3">
              <button v-if="stkOk === true || stkOk === false" class="hms-btn hms-btn-secondary" @click="resetStkForm">Send another</button>
              <button class="hms-btn hms-btn-primary ms-auto" @click="closeStk" :disabled="stkSending && stkOk === null">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ NEW INVOICE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showNewInvoice" @click.self="showNewInvoice=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-file-plus me-2"></i>New Invoice</span>
            <button class="hms-modal-close" @click="showNewInvoice=false">&times;</button>
          </div>
          <div v-if="newInvoiceError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ newInvoiceError }}</div>

          <!-- Patient -->
          <div class="mb-3">
            <label class="hms-label">Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="patientSearch" placeholder="Search name, patient no, phone, ID..." @input="onPatientSearch" />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }}</div>
              </div>
            </div>
            <div v-if="newInvoice.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>
              {{ newInvoice.patient_name }} <span class="hms-muted-sm ms-1">({{ newInvoice.patient_no }})</span>
              <button class="hms-modal-close ms-auto" @click="clearPatient" title="Clear">&times;</button>
            </div>
          </div>

          <div class="hms-section-label">LINE ITEMS</div>
          <div v-for="(line, li) in newInvoice.lines" :key="li" class="hms-inv-line">
            <div class="row g-2 align-items-end">
              <div class="col-md-5">
                <label class="hms-label" v-if="li === 0">Service / Description</label>
                <input class="hms-input" list="hms-services-list"
                       v-model="line._search"
                       @change="onServicePicked(line)"
                       @input="onServiceTyping(line)"
                       placeholder="Type to search — e.g. consult, lab, [TEST]…" />
                <input v-if="!line.service_id" class="hms-input mt-1" v-model="line.item_description" placeholder="Custom description (free text)" />
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Qty</label>
                <input type="number" class="hms-input" v-model.number="line.quantity" min="1" />
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Unit Price</label>
                <input type="number" class="hms-input" v-model.number="line.unit_price" min="0" step="0.01" />
                <div v-if="line._rate_card" class="hms-rate-pill" :title="line._rate_card.card_name + ' — ' + line._rate_card.rule">
                  <i class="mdi mdi-handshake-outline me-1"></i>
                  {{ line._rate_card.provider_name }}: KES {{ formatMoney(line._rate_card.catalogue_price) }} → KES {{ formatMoney(line._rate_card.negotiated_price) }} <strong>(save {{ formatMoney(line._rate_card.savings) }})</strong>
                </div>
              </div>
              <div class="col-md-2">
                <label class="hms-label" v-if="li === 0">Total</label>
                <div class="hms-input-readonly">KES {{ formatMoney(line.quantity * line.unit_price) }}</div>
              </div>
              <div class="col-md-1">
                <button class="hms-btn hms-btn-sm hms-btn-red" @click="removeLine(li)" :disabled="newInvoice.lines.length === 1">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>
          <button class="hms-btn hms-btn-blue hms-btn-sm mt-2" @click="addLine">
            <i class="mdi mdi-plus me-1"></i>Add line
          </button>

          <!-- Shared service typeahead list (used by every line) -->
          <datalist id="hms-services-list">
            <option v-for="s in services" :key="s.service_id" :value="serviceLabel(s)">{{ s.category }}</option>
          </datalist>

          <div class="hms-totals mt-3">
            <div><span>Subtotal</span><span>KES {{ formatMoney(newInvoiceSubtotal) }}</span></div>
            <div>
              <span>Discount</span>
              <span><input type="number" class="hms-input-inline" v-model.number="newInvoice.discount_amount" min="0" step="0.01" /></span>
            </div>
            <div class="hms-totals-total"><span>Total</span><span>KES {{ formatMoney(newInvoiceTotal) }}</span></div>
          </div>

          <div class="mb-3 mt-3"><label class="hms-label">Notes</label>
            <textarea class="hms-input" rows="2" v-model="newInvoice.notes" placeholder="Optional"></textarea>
          </div>

          <div class="mb-3">
            <label class="hms-check">
              <input type="checkbox" v-model="newInvoice.send_pay_link" :true-value="1" :false-value="0" />
              <span class="ms-1">
                Send pay link to patient via SMS + email after generating
                <span class="hms-muted-sm ms-1">(patient can pay via M-Pesa from any phone; link is shareable)</span>
              </span>
            </label>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitNewInvoice" :disabled="processing || !newInvoice.patient_id">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Generating...' : 'Generate Invoice' }}
          </button>
        </div>
      </div>

      <!-- ============ ADD SERVICE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showSvcForm" @click.self="showSvcForm=false">
        <div class="hms-modal" style="max-width:560px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-2"></i>Add Service</span>
            <button class="hms-modal-close" @click="showSvcForm=false">&times;</button>
          </div>
          <div v-if="svcError" class="hms-alert hms-alert-danger">{{ svcError }}</div>

          <div class="row g-3">
            <div class="col-md-8"><label class="hms-label">Service Name *</label>
              <input class="hms-input" v-model="svcForm.service_name" placeholder="e.g. Outpatient Consultation" />
            </div>
            <div class="col-md-4"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="svcForm.service_code" placeholder="OPD-MO" />
            </div>
            <div class="col-md-6"><label class="hms-label">Category</label>
              <select class="hms-input" v-model="svcForm.category">
                <option value="consultation">Consultation</option>
                <option value="procedure">Procedure</option>
                <option value="lab">Lab</option>
                <option value="radiology">Radiology</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="ward">Ward</option>
                <option value="theatre">Theatre</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Price (KES) *</label>
              <input type="number" class="hms-input" v-model.number="svcForm.price" min="0" step="0.01" />
            </div>
            <div class="col-md-3"><label class="hms-label">Insurance Price</label>
              <input type="number" class="hms-input" v-model.number="svcForm.insurance_price" min="0" step="0.01" />
            </div>
            <div class="col-md-6"><label class="hms-check"><input type="checkbox" v-model="svcForm.is_taxable" :true-value="1" :false-value="0" /> Taxable</label></div>
            <div class="col-md-6" v-if="svcForm.is_taxable == 1"><label class="hms-label">Tax Rate (%)</label>
              <input type="number" class="hms-input" v-model.number="svcForm.tax_rate" min="0" step="0.01" />
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100 mt-3" @click="saveService" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Add Service
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-rate-pill { display: inline-block; margin-top: 4px; padding: 2px 8px; background: rgba(124, 58, 237, 0.10); color: #6d28d9; border: 1px solid rgba(124, 58, 237, 0.30); border-radius: 12px; font-size: 10.5px; line-height: 1.35; }
.hms-rate-row td { background: rgba(124, 58, 237, 0.04); border-top: none !important; padding-top: 0 !important; padding-bottom: 6px !important; }
.hms-rate-savings { font-size: 11.5px; color: #6d28d9; }
.hms-strike { text-decoration: line-through; color: var(--hms-s, #94a3b8); }
.hms-insurance-summary { display: flex; justify-content: space-between; align-items: center; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.30); border-radius: 6px; padding: 6px 10px; margin: 6px 0; font-size: 12.5px; color: #065f46; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-text-success { color: var(--hms-success); }
.hms-text-danger  { color: var(--hms-danger); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--hms-h); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 160px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-red { color: var(--hms-danger); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-pager { display: flex; align-items: center; justify-content: center; gap: 14px; padding: 12px 0 4px; }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input-readonly { background: var(--hms-bg3); border: 1px solid var(--hms-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; }
.hms-input-inline { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 6px; padding: 4px 8px; color: var(--hms-h); font-size: 13px; outline: none; width: 110px; text-align: right; }
.hms-input-inline:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; margin-top: 8px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }
.hms-btn-link { background: none; border: none; color: var(--hms-primary); font-size: 11px; cursor: pointer; padding: 2px 0; text-decoration: underline; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-orange  { background: rgba(245,130,31,.15); color: var(--hms-accent); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-green  { background: var(--hms-success); color: #fff; }
.hms-btn-green:hover:not(:disabled) { filter: brightness(1.08); }
.hms-btn-secondary { background: var(--hms-bg3); color: var(--hms-h); border: 1px solid var(--hms-br); }
.hms-btn-secondary:hover:not(:disabled) { background: var(--hms-bg2); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 800px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

.hms-totals { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; margin-top: 12px; }
.hms-totals > div { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
.hms-totals-total { font-weight: 700; font-size: 15px; color: var(--hms-h); border-top: 1px solid var(--hms-br); padding-top: 8px !important; margin-top: 4px; }
.hms-totals-balance { font-weight: 700; font-size: 15px; border-top: 1px solid var(--hms-br); padding-top: 8px !important; margin-top: 4px; }

.hms-inv-line { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px; margin-bottom: 8px; }

.hms-focus-card { border-left: 4px solid var(--hms-primary); background: var(--hms-primary-lt); }
.hms-focus-icon { font-size: 36px; color: var(--hms-primary); }
.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,.08); }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-alert-info   { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }

.hms-stk-status { display: flex; gap: 14px; align-items: center; padding: 16px 18px; border-radius: 12px; border: 1px solid var(--hms-br); }
.hms-stk-icon i { font-size: 32px; }
.hms-stk-msg { font-size: 14px; font-weight: 500; line-height: 1.4; color: var(--hms-h); }
.hms-stk-pending { background: rgba(245,158,11,.08); border-color: rgba(245,158,11,.3); }
.hms-stk-pending .hms-stk-icon i { color: #f59e0b; }
.hms-stk-ok { background: rgba(40,167,69,.08); border-color: rgba(40,167,69,.3); }
.hms-stk-ok .hms-stk-icon i { color: var(--hms-success); }
.hms-stk-bad { background: rgba(220,53,69,.08); border-color: rgba(220,53,69,.3); }
.hms-stk-bad .hms-stk-icon i { color: var(--hms-danger); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

@media (max-width: 768px) {
  .hms-stat-row .hms-stat { min-width: calc(50% - 6px); }
  .hms-modal { padding: 18px; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "BillingManagement",
  components: { Navigation, SaveAndQueue },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "invoices",
      i18n: useI18n(),

      invoices: [],
      payments: [],
      services: [],

      invSearch: "",
      statusFilter: "",
      invPage: 1, payPage: 1, pageSize: 20,

      // Patient focus
      focusedPatient: null,           // { patient_id, first_name, last_name, patient_no, phone }
      focusSearch: "",
      focusResults: [],
      focusSearchTimer: null,
      paySearch: "",
      payMethodFilter: "",
      svcSearch: "",
      svcCategoryFilter: "",

      // Invoice detail
      showInvoice: false,
      invoiceLoading: false,
      invoiceDetail: null,
      invoiceItems: [],
      insuranceSavings: null,
      invoicePayments: [],

      // Record payment
      showPayment: false,
      paymentForm: null,
      paymentError: "",

      // Discount
      showDiscount: false,
      discountAmount: 0,
      discountError: "",

      // M-Pesa STK
      showStk: false,
      stkForm: null,
      stkSending: false,
      stkError: "",
      stkStatus: "",
      stkOk: null,
      stkCheckoutId: "",
      stkPollAbort: false,

      // Pay-link send state
      sendingPayLink: false,

      // Add-items modal
      showAddItems: false,
      addItems: null,
      addItemsError: '',

      // New invoice
      showNewInvoice: false,
      newInvoiceError: "",
      newInvoice: this.emptyNewInvoice(),
      patientSearch: "",
      patientSearchTimer: null,
      patientResults: [],

      // Service form
      showSvcForm: false,
      svcError: "",
      svcForm: this.emptySvcForm(),

      // Toast
      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    totalCollected()   { return this.invoices.reduce((s, i) => s + parseFloat(i.paid_amount || 0), 0); },
    totalOutstanding() { return this.invoices.reduce((s, i) => s + parseFloat(i.balance || 0), 0); },
    filteredInvoices() {
      var list = this.invoices;
      if (this.focusedPatient) {
        var pid = String(this.focusedPatient.patient_id);
        list = list.filter(i => String(i.patient_id) === pid);
      }
      if (this.statusFilter) list = list.filter(i => i.payment_status === this.statusFilter);
      var q = this.invSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(i => ((i.invoice_number || "") + " " + (i.patient_name || "") + " " + (i.patient_no || "")).toLowerCase().indexOf(q) >= 0);
      }
      return list;
    },
    addItemsSubtotal() {
      if (!this.addItems) return 0;
      return this.addItems.lines.reduce((s, l) => s + (parseFloat(l.quantity || 0) * parseFloat(l.unit_price || 0)), 0);
    },
    addItemsNewBalance() {
      if (!this.invoiceDetail) return 0;
      return parseFloat(this.invoiceDetail.balance || 0) + this.addItemsSubtotal;
    },
    focusedPatientStats() {
      if (!this.focusedPatient) return { invoice_count: 0, total_billed: 0, total_paid: 0, total_outstanding: 0 };
      var pid = String(this.focusedPatient.patient_id);
      var pinvs = this.invoices.filter(i => String(i.patient_id) === pid);
      return {
        invoice_count:     pinvs.length,
        total_billed:      pinvs.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0),
        total_paid:        pinvs.reduce((s, i) => s + parseFloat(i.paid_amount  || 0), 0),
        total_outstanding: pinvs.reduce((s, i) => s + parseFloat(i.balance      || 0), 0)
      };
    },
    filteredPayments() {
      var list = this.payments;
      if (this.payMethodFilter) list = list.filter(p => p.payment_method === this.payMethodFilter);
      var q = this.paySearch.trim().toLowerCase();
      if (q) {
        list = list.filter(p => ((p.patient_name || "") + " " + (p.payment_number || "") + " " + (p.mpesa_reference || "") + " " + (p.card_reference || "") + " " + (p.bank_reference || "")).toLowerCase().indexOf(q) >= 0);
      }
      return list;
    },
    filteredPaymentsTotal() {
      return this.filteredPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    },
    invPageCount() { return Math.max(1, Math.ceil(this.filteredInvoices.length / this.pageSize)); },
    pagedInvoices() {
      var p = Math.min(this.invPage, this.invPageCount);
      return this.filteredInvoices.slice((p - 1) * this.pageSize, p * this.pageSize);
    },
    payPageCount() { return Math.max(1, Math.ceil(this.filteredPayments.length / this.pageSize)); },
    pagedPayments() {
      var p = Math.min(this.payPage, this.payPageCount);
      return this.filteredPayments.slice((p - 1) * this.pageSize, p * this.pageSize);
    },
    filteredServices() {
      var list = this.services;
      if (this.svcCategoryFilter) list = list.filter(s => s.category === this.svcCategoryFilter);
      var q = this.svcSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(s => ((s.service_name || "") + " " + (s.service_code || "") + " " + (s.category || "")).toLowerCase().indexOf(q) >= 0);
      }
      return list;
    },
    newInvoiceSubtotal() {
      return this.newInvoice.lines.reduce((s, l) => s + (parseFloat(l.quantity || 0) * parseFloat(l.unit_price || 0)), 0);
    },
    newInvoiceTotal() {
      return Math.max(0, this.newInvoiceSubtotal - parseFloat(this.newInvoice.discount_amount || 0));
    },
    canSendInvoiceStk() {
      if (!this.stkForm) return false;
      var phone = String(this.stkForm.phone || "").replace(/\s+/g, "");
      var phoneOk  = /^(?:\+?254|0)(?:7|1)\d{8}$/.test(phone);
      var amount   = Number(this.stkForm.amount);
      var amountOk = amount >= 1 && amount <= parseFloat(this.stkForm.balance);
      return phoneOk && amountOk && !!this.stkForm.invoice_id;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll().then(() => {
      // Deep links from the patient chart:
      //   /billing?patient_id=78           → open the New Invoice modal with patient prefilled
      //   /billing?open_invoice=123        → open invoice 123's detail
      var q = this.$route.query || {};
      if (q.open_invoice) {
        var inv = this.invoices.find(i => String(i.invoice_id) === String(q.open_invoice));
        if (inv) this.openInvoice(inv);
        else this.openInvoice({ invoice_id: q.open_invoice });
      }
      if (q.patient_id) {
        var pid = q.patient_id;
        // Lookup patient + set focus + open New Invoice with the patient prefilled
        executeJson({ function: 'getPatient', patient_id: pid }, hms).then(r => {
          if (r.data && r.data.success && r.data.data && r.data.data.Patient) {
            var p = r.data.data.Patient;
            this.pickFocusedPatient(p);
            this.openNewInvoice();
            this.pickPatient({
              patient_id: p.patient_id,
              first_name: p.first_name,
              last_name:  p.last_name,
              patient_no: p.patient_no
            });
          }
        }).catch(() => {});
      }
    });
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  watch: {
    invSearch()       { this.invPage = 1; },
    statusFilter()    { this.invPage = 1; },
    focusedPatient()  { this.invPage = 1; },
    paySearch()       { this.payPage = 1; },
    payMethodFilter() { this.payPage = 1; }
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    printInvoice(id) { window.open(this.$router.resolve({ name: 'print', params: { type: 'invoice', id }, query: { auto: '1' } }).href, '_blank'); },
    printReceipt(id) { window.open(this.$router.resolve({ name: 'print', params: { type: 'receipt', id }, query: { auto: '1' } }).href, '_blank'); },
    emptyNewInvoice() {
      return {
        patient_id: null, patient_name: "", patient_no: "",
        notes: "", discount_amount: 0,
        send_pay_link: 1,   // biller controls — default on, can uncheck
        lines: [ this.emptyLine() ]
      };
    },
    emptyLine() {
      return { service_id: "", item_description: "", item_category: "other", quantity: 1, unit_price: 0, discount: 0, tax_rate: 0, _search: "", _catalogue_price: null, _rate_card: null };
    },
    emptySvcForm() {
      return {
        service_id: null, service_name: "", service_code: "", category: "consultation",
        price: 0, insurance_price: 0, is_taxable: 0, tax_rate: 0
      };
    },

    countByStatus(s) { return this.invoices.filter(i => i.payment_status === s).length; },
    setStatusFilter(s) { this.tab = "invoices"; this.statusFilter = s; },

    // ===== PATIENT FOCUS =====
    onFocusSearch() {
      if (this.focusSearchTimer) { clearTimeout(this.focusSearchTimer); this.focusSearchTimer = null; }
      if (!this.focusSearch || this.focusSearch.length < 2) { this.focusResults = []; return; }
      var self = this;
      this.focusSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.focusSearch.trim() }, hms);
          if (r.data && r.data.success) self.focusResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) {}
        self.focusSearchTimer = null;
      }, 300);
    },
    pickFocusedPatient(p) {
      this.focusedPatient = {
        patient_id:  p.patient_id,
        first_name:  p.first_name,
        last_name:   p.last_name,
        patient_no:  p.patient_no,
        phone:       p.phone || ''
      };
      this.focusSearch = '';
      this.focusResults = [];
      this.statusFilter = ''; this.invSearch = '';
    },
    clearFocusedPatient() {
      this.focusedPatient = null;
      this.focusSearch = '';
      this.focusResults = [];
    },
    openNewInvoiceForFocused() {
      if (!this.focusedPatient) return;
      this.openNewInvoice();
      this.pickPatient({
        patient_id: this.focusedPatient.patient_id,
        first_name: this.focusedPatient.first_name,
        last_name:  this.focusedPatient.last_name,
        patient_no: this.focusedPatient.patient_no
      });
    },

    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "0.00";
      return Number(v).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined, { day:'2-digit', month:'short', year:'numeric' }); } catch(e) { return d; }
    },
    formatDateTime(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleString(undefined, { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch(e) { return d; }
    },
    formatMethod(m) {
      var map = { cash:'Cash', mpesa:'M-Pesa', card:'Card', bank_transfer:'Bank', insurance:'Insurance', corporate:'Corporate', waiver:'Waiver' };
      return map[m] || m;
    },
    methodIcon(m) {
      switch (m) {
        case 'mpesa':         return 'mdi mdi-cellphone-wireless';
        case 'card':          return 'mdi mdi-credit-card';
        case 'bank_transfer': return 'mdi mdi-bank';
        case 'cash':          return 'mdi mdi-cash';
        case 'insurance':     return 'mdi mdi-shield-account';
        case 'corporate':     return 'mdi mdi-domain';
        case 'waiver':        return 'mdi mdi-tag-off';
        default:              return 'mdi mdi-cash';
      }
    },
    methodBadge(m) {
      switch (m) {
        case 'mpesa':         return 'hms-badge-success';
        case 'card':          return 'hms-badge-info';
        case 'bank_transfer': return 'hms-badge-info';
        case 'cash':          return 'hms-badge-neutral';
        case 'insurance':     return 'hms-badge-purple';
        case 'corporate':     return 'hms-badge-orange';
        case 'waiver':        return 'hms-badge-amber';
        default:              return 'hms-badge-neutral';
      }
    },
    payStatusBadge(s) {
      var m = { paid:'hms-badge-success', partial:'hms-badge-info', unpaid:'hms-badge-amber', waived:'hms-badge-purple', insurance_pending:'hms-badge-orange' };
      return m[s] || 'hms-badge-neutral';
    },
    payStatusLabel(s) {
      var m = { paid:'Paid', partial:'Partial', unpaid:'Unpaid', waived:'Waived', insurance_pending:'Insurance' };
      return m[s] || s;
    },
    payTxnStatusBadge(s) {
      var m = { completed:'hms-badge-success', pending:'hms-badge-amber', reversed:'hms-badge-danger', failed:'hms-badge-danger' };
      return m[s] || 'hms-badge-neutral';
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2800);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([this.loadInvoices(), this.loadPayments(), this.loadServices()]); }
      catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadInvoices() {
      try {
        var r = await executeJson({ function: "getInvoices", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.invoices = (r.data.data && r.data.data.Invoices) || [];
      } catch(e) { console.error(e); }
    },
    async loadPayments() {
      try {
        var r = await executeJson({ function: "getPayments", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.payments = (r.data.data && r.data.data.Payments) || [];
      } catch(e) { console.error(e); }
    },
    async loadServices() {
      try {
        var r = await executeJson({ function: "getServiceCatalog", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.services = (r.data.data && r.data.data.Services) || [];
      } catch(e) { console.error(e); }
    },

    // ===== INVOICE DETAIL =====
    async openInvoice(inv) {
      this.showInvoice = true;
      this.invoiceLoading = true;
      this.invoiceDetail = null;
      this.invoiceItems = [];
      this.invoicePayments = [];
      this.insuranceSavings = null;
      try {
        var r = await executeJson({ function: "getPatientInvoice", invoice_id: inv.invoice_id }, hms);
        if (r.data && r.data.success) {
          this.invoiceDetail    = r.data.data.Invoice;
          this.invoiceItems     = r.data.data.Items || [];
          this.invoicePayments  = r.data.data.Payments || [];
          this.insuranceSavings = r.data.data.InsuranceSavings || null;
        }
      } catch(e) { this.showToast("Failed to load invoice", true); }
      this.invoiceLoading = false;
    },
    closeInvoice() {
      this.showInvoice = false;
      this.invoiceDetail = null;
      this.invoiceItems = [];
      this.invoicePayments = [];
      this.insuranceSavings = null;
    },

    // ===== PAYMENT =====
    openPayment(inv) {
      this.paymentError = "";
      this.paymentForm = {
        invoice_id:      inv.invoice_id,
        invoice_number:  inv.invoice_number,
        patient_id:      inv.patient_id,
        patient_name:    inv.patient_name,
        total_amount:    inv.total_amount,
        paid_amount:     inv.paid_amount,
        balance:         inv.balance,
        amount:          parseFloat(inv.balance || 0),
        payment_method:  "cash",
        mpesa_reference: "",
        card_reference:  "",
        bank_reference:  "",
        waiver_reason:   "",
        waiver_narrative:"",
        waiver_doc:      "",
        notes:           ""
      };
      this.showPayment = true;
    },
    async doRecordPayment() {
      this.paymentError = "";
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) { this.paymentError = "Enter a valid amount."; return; }
      if (this.paymentForm.payment_method === 'mpesa' && !this.paymentForm.mpesa_reference) { this.paymentError = "M-Pesa reference is required."; return; }
      if (this.paymentForm.payment_method === 'card' && !this.paymentForm.card_reference)   { this.paymentError = "Card reference is required."; return; }
      if (this.paymentForm.payment_method === 'bank_transfer' && !this.paymentForm.bank_reference) { this.paymentError = "Bank reference is required."; return; }
      if (this.paymentForm.payment_method === 'waiver') {
        if (!this.paymentForm.waiver_reason || !this.paymentForm.waiver_narrative) { this.paymentError = "Waiver reason + narrative required."; return; }
      }

      this.processing = true;
      try {
        // For waivers, raise an approval request first; payment will record on superintendent sign-off.
        if (this.paymentForm.payment_method === 'waiver') {
          var rw = await executeJson({
            function:        "requestPaymentWaiver",
            invoice_id:      this.paymentForm.invoice_id,
            patient_id:      this.paymentForm.patient_id,
            amount_waived:   this.paymentForm.amount,
            reason_code:     this.paymentForm.waiver_reason,
            narrative:       this.paymentForm.waiver_narrative,
            doc_path:        this.paymentForm.waiver_doc
          }, hms);
          if (rw.data && rw.data.success) {
            this.showToast("Waiver request submitted — pending Charge Nurse approval");
            this.showPayment = false;
          } else { this.paymentError = (rw.data && rw.data.message) || "Failed."; }
          this.processing = false;
          return;
        }
        var r = await executeJson({
          function:        "recordPayment",
          invoice_id:      this.paymentForm.invoice_id,
          patient_id:      this.paymentForm.patient_id,
          amount:          this.paymentForm.amount,
          payment_method:  this.paymentForm.payment_method,
          mpesa_reference: this.paymentForm.mpesa_reference,
          card_reference:  this.paymentForm.card_reference,
          bank_reference:  this.paymentForm.bank_reference,
          notes:           this.paymentForm.notes
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Payment recorded");
          // If cashier picked a "Save & queue to ___", route the patient now.
          var pid = this.paymentForm.patient_id;
          if (this.$refs.sqPayment && pid) {
            var q = await this.$refs.sqPayment.queueIfSelected(pid);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          this.showPayment = false;
          var rid = this.paymentForm.invoice_id;
          await Promise.all([this.loadInvoices(), this.loadPayments()]);
          if (this.showInvoice) {
            await this.openInvoice({ invoice_id: rid });
          }
        } else { this.paymentError = r.data.message || "Failed."; }
      } catch(e) { this.paymentError = "Connection error."; }
      this.processing = false;
    },

    // ===== DISCOUNT =====
    openDiscount() {
      if (!this.invoiceDetail) return;
      this.discountAmount = parseFloat(this.invoiceDetail.discount_amount || 0);
      this.discountError = "";
      this.showDiscount = true;
    },
    async doApplyDiscount() {
      this.discountError = "";
      this.processing = true;
      try {
        var r = await executeJson({
          function:        "applyDiscount",
          invoice_id:      this.invoiceDetail.invoice_id,
          discount_amount: this.discountAmount
        }, hms);
        if (r.data && r.data.success) {
          this.showToast("Discount applied");
          this.showDiscount = false;
          var rid = this.invoiceDetail.invoice_id;
          await this.loadInvoices();
          await this.openInvoice({ invoice_id: rid });
        } else { this.discountError = r.data.message || "Failed."; }
      } catch(e) { this.discountError = "Connection error."; }
      this.processing = false;
    },

    // Open the STK modal from inside the Record Payment modal, preserving any typed-in amount
    openStkFromPayment() {
      if (!this.paymentForm) return;
      var typedAmount = parseFloat(this.paymentForm.amount || 0);
      var bal         = parseFloat(this.paymentForm.balance || 0);
      var inv = {
        invoice_id:     this.paymentForm.invoice_id,
        invoice_number: this.paymentForm.invoice_number,
        patient_name:   this.paymentForm.patient_name,
        balance:        bal,
        phone:          this.invoiceDetail ? this.invoiceDetail.phone : ''
      };
      // Close the Record Payment modal and open the STK modal pre-filled
      this.showPayment = false;
      this.openStk(inv);
      // If they had already typed a partial-payment amount, honour it
      if (typedAmount > 0 && typedAmount <= bal) {
        this.stkForm.amount = Math.max(1, Math.floor(typedAmount));
      }
    },

    // ===== ADD ITEMS TO EXISTING INVOICE =====
    openAddItems() {
      if (!this.invoiceDetail) return;
      this.addItemsError = '';
      this.addItems = { lines: [ this.emptyLine() ] };
      this.showAddItems = true;
    },
    addAddLine() {
      this.addItems.lines.push(this.emptyLine());
    },
    removeAddLine(idx) {
      this.addItems.lines.splice(idx, 1);
    },
    async submitAddItems() {
      this.addItemsError = '';
      if (!this.invoiceDetail || !this.addItems) return;
      var lines = this.addItems.lines.filter(l => (l.service_id || l.item_description) && l.quantity > 0 && l.unit_price >= 0);
      if (!lines.length) { this.addItemsError = 'Add at least one valid line.'; return; }

      var items = lines.map(l => ({
        service_id:       l.service_id || null,
        item_description: l.item_description,
        item_category:    l.item_category,
        quantity:         l.quantity,
        unit_price:       l.unit_price,
        discount:         l.discount  || 0,
        tax_rate:         l.tax_rate  || 0
      }));

      this.processing = true;
      try {
        var r = await executeJson({
          function:   'addInvoiceItems',
          invoice_id: this.invoiceDetail.invoice_id,
          items:      items
        }, hms);
        if (r.data && r.data.success) {
          this.showToast((r.data && r.data.message) || 'Items added');
          this.showAddItems = false;
          var rid = this.invoiceDetail.invoice_id;
          await Promise.all([this.loadInvoices(), this.openInvoice({ invoice_id: rid })]);
        } else {
          this.addItemsError = (r.data && r.data.message) || 'Failed.';
        }
      } catch (e) { this.addItemsError = 'Connection error.'; }
      this.processing = false;
    },

    // ===== PAY LINK =====
    async sendPayLink(inv) {
      if (!inv || !inv.invoice_id) return;
      this.sendingPayLink = true;
      try {
        var r = await executeJson({ function: 'sendInvoicePayLink', invoice_id: inv.invoice_id }, hms);
        if (r.data && r.data.success) {
          this.showToast('Pay link sent to patient (SMS + email).');
        } else {
          this.showToast((r.data && r.data.message) || 'Could not send pay link.', true);
        }
      } catch (e) { this.showToast('Connection error.', true); }
      this.sendingPayLink = false;
    },

    // ===== M-PESA STK =====
    openStk(inv) {
      this.stkError = "";
      this.stkStatus = "";
      this.stkOk = null;
      this.stkCheckoutId = "";
      this.stkPollAbort = false;
      var bal = parseFloat(inv.balance || 0);
      this.stkForm = {
        invoice_id:     inv.invoice_id,
        invoice_number: inv.invoice_number,
        patient_name:   inv.patient_name,
        balance:        bal,
        phone:          inv.phone || "",
        amount:         Math.max(1, Math.floor(bal))
      };
      this.showStk = true;
    },
    closeStk() {
      if (this.stkSending && this.stkOk === null) return; // don't close mid-send
      this.stkPollAbort = true;
      this.showStk = false;
      this.stkForm = null;
    },
    resetStkForm() {
      this.stkStatus = "";
      this.stkOk = null;
      this.stkCheckoutId = "";
      this.stkError = "";
      this.stkPollAbort = false;
    },
    async sendInvoiceStk() {
      if (!this.canSendInvoiceStk) return;
      this.stkError = "";
      this.stkSending = true;
      this.stkStatus = "⏳ Sending STK push to phone…";
      this.stkOk = null;
      this.stkPollAbort = false;
      try {
        var r = await executeJson({
          function:   "invoiceStkPush",
          invoice_id: this.stkForm.invoice_id,
          phone:      this.stkForm.phone,
          amount:     this.stkForm.amount
        }, hms);
        if (!r.data || !r.data.success) {
          this.stkStatus = "❌ " + ((r.data && r.data.message) || "STK push failed.");
          this.stkOk = false; this.stkSending = false; return;
        }
        var checkoutId = r.data.data && r.data.data.checkout_request_id;
        if (!checkoutId) {
          this.stkStatus = "⚠ STK sent but no checkout id returned — cannot track status.";
          this.stkOk = false; this.stkSending = false; return;
        }
        this.stkCheckoutId = checkoutId;
        this.stkStatus = "📲 STK push sent. Customer should enter M-Pesa PIN within ~90 seconds.";
        await this.pollInvoiceStk(checkoutId);
      } catch (e) {
        this.stkStatus = "❌ Connection error.";
        this.stkOk = false;
      }
      this.stkSending = false;
    },
    async pollInvoiceStk(checkoutId) {
      for (var i = 0; i < 30; i++) {
        if (this.stkPollAbort) return;
        await new Promise(function(r){ setTimeout(r, 3000); });
        if (this.stkPollAbort) return;
        try {
          var r = await executeJson({ function: "getStkPushStatus", checkout_request_id: checkoutId }, hms);
          var d = r.data && r.data.data;
          if (!d) continue;
          if (d.status === "success") {
            this.stkStatus = "✅ Payment received! Receipt " + (d.mpesa_receipt || "—") + " · KES " + d.amount;
            this.stkOk = true;
            await this.onStkSettled();
            return;
          }
          if (d.status === "cancelled") {
            this.stkStatus = "🚫 Cancelled by customer (pressed Cancel on phone).";
            this.stkOk = false; return;
          }
          if (d.status === "failed") {
            this.stkStatus = "❌ " + (d.human_message || d.response_description || "STK failed") + (d.response_code ? " (code " + d.response_code + ")" : "");
            this.stkOk = false; return;
          }
          if (d.status === "timeout") {
            this.stkStatus = "⌛ Timed out — customer did not respond on phone.";
            this.stkOk = false; return;
          }
          this.stkStatus = "📲 " + (d.human_message || "Waiting for PIN…") + "  (" + (d.seconds_since_initiated || 0) + "s)";
        } catch (e) { /* keep polling */ }
      }
      // 90s with no callback — ask Safaricom directly
      if (this.stkPollAbort) return;
      this.stkStatus = "⏳ No callback after 90s. Querying Safaricom directly…";
      try {
        await executeJson({ function: "queryStkPushFromSafaricom", checkout_request_id: checkoutId }, hms);
        var r2 = await executeJson({ function: "getStkPushStatus", checkout_request_id: checkoutId }, hms);
        var d2 = r2.data && r2.data.data;
        if (d2 && d2.status === "success") {
          this.stkStatus = "✅ Payment received (via direct query). Receipt " + d2.mpesa_receipt;
          this.stkOk = true;
          await this.onStkSettled();
        } else if (d2) {
          this.stkStatus = "🔍 " + (d2.human_message || d2.response_description || d2.status);
          this.stkOk = d2.status === "success";
        } else {
          this.stkStatus = "⚠ Could not determine final status.";
          this.stkOk = false;
        }
      } catch (e) {
        this.stkStatus = "⚠ Timed out and direct query failed.";
        this.stkOk = false;
      }
    },
    async onStkSettled() {
      // Backend callback auto-records the payment & updates the invoice — refresh views
      var invId = this.stkForm ? this.stkForm.invoice_id : null;
      try {
        await Promise.all([this.loadInvoices(), this.loadPayments()]);
        if (this.showInvoice && invId) {
          await this.openInvoice({ invoice_id: invId });
        }
      } catch (e) { /* refresh failure is non-fatal — status box still shows success */ }
      this.showToast("Payment received via M-Pesa");
    },

    // ===== NEW INVOICE =====
    openNewInvoice() {
      this.newInvoice = this.emptyNewInvoice();
      this.newInvoiceError = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.showNewInvoice = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) self.patientResults = (r.data.data && r.data.data.Patients) || [];
        } catch(e) { /* silent */ }
      }, 300);
    },
    pickPatient(p) {
      this.newInvoice.patient_id   = p.patient_id;
      this.newInvoice.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.newInvoice.patient_no   = p.patient_no;
      this.patientResults = [];
      this.patientSearch  = "";
    },
    clearPatient() {
      this.newInvoice.patient_id = null;
      this.newInvoice.patient_name = "";
      this.newInvoice.patient_no = "";
    },
    addLine() { this.newInvoice.lines.push(this.emptyLine()); },
    removeLine(idx) { this.newInvoice.lines.splice(idx, 1); },
    applyServiceToLine(line) {
      var s = this.services.find(x => x.service_id == line.service_id);
      if (s) {
        line.item_description = s.service_name;
        line.item_category    = s.category;
        line.unit_price       = parseFloat(s.price) || 0;
        line.tax_rate         = parseFloat(s.tax_rate) || 0;
        line._search          = this.serviceLabel(s);
      }
    },
    serviceLabel(s) {
      return s.service_name + ' — KES ' + this.formatMoney(s.price);
    },
    onServiceTyping(line) {
      // User is editing; clear binding so a partial match doesn't auto-apply.
      // @change still fires when they pick from the datalist.
    },
    activeInsuranceProvider() {
      // Prefer the invoice's insurance provider (e.g. when adding to an existing invoice);
      // else fall back to the new-invoice form's selected provider; else patient's saved one.
      var inv = this.invoiceDetail;
      var p   = (inv && inv.insurance_provider)
             || (this.newInvoice && this.newInvoice.insurance_provider)
             || (this.paymentForm && this.paymentForm.insurance_provider)
             || (inv && inv.patient_insurance_provider)
             || '';
      return (p || '').trim();
    },
    async applyInsuranceRate(line, s) {
      var prov = this.activeInsuranceProvider();
      if (!prov) return; // no insurance → leave catalogue price
      try {
        var r = await executeJson({
          function: "lookupInsuranceRate",
          service_id: s.service_id || '',
          service_code: s.service_code || '',
          catalogue_price: parseFloat(s.price) || 0,
          provider_name: prov
        }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.has_rate) {
          line.unit_price = parseFloat(r.data.data.final_price) || line.unit_price;
          line._rate_card = r.data.data; // { provider_name, catalogue_price, negotiated_price, savings, rule, card_name }
        }
      } catch(e) { /* silent — fall through to catalogue price */ }
    },
    onServicePicked(line) {
      var label = (line._search || '').trim();
      if (!label) {
        line.service_id = '';
        line.item_description = '';
        line._catalogue_price = null;
        line._rate_card = null;
        return;
      }
      // Match by the full label first (datalist pick), then by bare name (typed)
      var byLabel = this.services.find(x => this.serviceLabel(x) === label);
      var s = byLabel || this.services.find(x => x.service_name.toLowerCase() === label.toLowerCase());
      if (s) {
        line.service_id        = s.service_id;
        line.item_description  = s.service_name;
        line.item_category     = s.category;
        line.unit_price        = parseFloat(s.price) || 0;
        line._catalogue_price  = parseFloat(s.price) || 0;
        line.tax_rate          = parseFloat(s.tax_rate) || 0;
        line._search           = this.serviceLabel(s);
        line._rate_card        = null;
        // Auto-lookup the negotiated rate if patient has an insurance provider.
        this.applyInsuranceRate(line, s);
      } else {
        // Free-text fallback — keep whatever the user typed as the description
        line.service_id = '';
        line.item_description = label;
        line._catalogue_price = null;
        line._rate_card = null;
      }
    },
    async submitNewInvoice() {
      if (this.processing) return; // double-click guard: 2nd click can land before the disabled re-render
      this.newInvoiceError = "";
      if (!this.newInvoice.patient_id) { this.newInvoiceError = "Pick a patient."; return; }
      var lines = this.newInvoice.lines.filter(l => (l.service_id || l.item_description) && l.quantity > 0 && l.unit_price >= 0);
      if (!lines.length) { this.newInvoiceError = "Add at least one line item."; return; }

      var items = lines.map(l => ({
        service_id:       l.service_id || null,
        item_description: l.item_description,
        item_category:    l.item_category,
        quantity:         l.quantity,
        unit_price:       l.unit_price,
        discount:         l.discount || 0,
        tax_rate:         l.tax_rate || 0
      }));

      this.processing = true;
      try {
        var r = await executeJson({
          function:        "generateInvoice",
          patient_id:      this.newInvoice.patient_id,
          items:           items,
          discount_amount: this.newInvoice.discount_amount || 0,
          notes:           this.newInvoice.notes,
          send_pay_link:   this.newInvoice.send_pay_link || 0
        }, hms);
        if (r.data && r.data.success) {
          var msg = "Invoice generated";
          if (r.data.data && r.data.data.link_sent) msg += " · Pay link sent to patient";
          this.showToast(msg);
          this.showNewInvoice = false;
          await this.loadInvoices();
        } else { this.newInvoiceError = r.data.message || "Failed."; }
      } catch(e) { this.newInvoiceError = "Connection error."; }
      this.processing = false;
    },

    // ===== SERVICE =====
    openAddService() {
      this.svcForm = this.emptySvcForm();
      this.svcError = "";
      this.showSvcForm = true;
    },
    async saveService() {
      this.svcError = "";
      if (!this.svcForm.service_name) { this.svcError = "Service name is required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createService" }, this.svcForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Service added");
          this.showSvcForm = false;
          await this.loadServices();
        } else { this.svcError = r.data.message || "Failed."; }
      } catch(e) { this.svcError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
